import React, { useState } from 'react';
import { Star, X } from 'lucide-react';
import { Button } from './Button';
import { clsx } from 'clsx';
import { api } from '../lib/axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface GroomingReviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    appointmentId: string;
    agencyName?: string;
    onSuccess: () => void;
}

export const GroomingReviewModal: React.FC<GroomingReviewModalProps> = ({ isOpen, onClose, appointmentId, agencyName, onSuccess }) => {
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { t } = useTranslation();

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (rating <= 0) {
            toast.error(t('reviews.selectRating', 'Please select a rating'));
            return;
        }

        setIsSubmitting(true);
        try {
            await api.post('/grooming/reviews', {
                appointmentId,
                rating,
                comment
            });
            toast.success(t('reviews.feedbackThanks', 'Thank you for your feedback!'));
            onSuccess();
            onClose();
        } catch (error: any) {
            toast.error(error.response?.data?.error || t('reviews.submitFailed', 'Failed to submit review'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const activeRating = hover || rating;

    const getRatingLabel = (r: number) => {
        if (r >= 5) return t('reviews.excellent', 'Excellent!');
        if (r >= 4) return t('reviews.veryGood', 'Very Good');
        if (r >= 3) return t('reviews.good', 'Good');
        if (r >= 2) return t('reviews.fair', 'Fair');
        if (r >= 1) return t('reviews.poor', 'Poor');
        return '';
    };

    const handleStarMouseMove = (e: React.MouseEvent<HTMLDivElement>, star: number) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        // Left half = half star (0.5, 1.5, ...), right half = full star
        const val = x < rect.width / 2 ? star - 0.5 : star;
        setHover(val);
    };

    const handleStarClick = (e: React.MouseEvent<HTMLDivElement>, star: number) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const val = x < rect.width / 2 ? star - 0.5 : star;
        setRating(val);
        setHover(0);
    };

    // Determine fill for each star position
    const getStarFill = (star: number, active: number): 'full' | 'half' | 'empty' => {
        if (active >= star) return 'full';
        if (active >= star - 0.5) return 'half';
        return 'empty';
    };

    return (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h2 className="font-bold text-slate-800">{t('reviews.rateGroomingService', 'Rate Grooming Service')}</h2>
                    <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded-full transition-colors">
                        <X size={18} className="text-slate-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="text-center">
                        <p className="text-sm text-slate-500 mb-4 uppercase tracking-widest font-black text-[10px]">{t('reviews.experiencePrompt', 'How was your experience with {{name}}?', { name: agencyName || t('reviews.theGroomer', 'the Groomer') })}</p>
                        <div className="flex justify-center gap-2">
                            {[1, 2, 3, 4, 5].map((star) => {
                                const fill = getStarFill(star, activeRating);
                                return (
                                    <div
                                        key={star}
                                        className="relative cursor-pointer transition-transform hover:scale-110 active:scale-95 w-10 h-10"
                                        onMouseMove={(e) => handleStarMouseMove(e, star)}
                                        onMouseLeave={() => setHover(0)}
                                        onClick={(e) => handleStarClick(e, star)}
                                    >
                                        {/* Background (empty) star */}
                                        <Star className="w-10 h-10 text-gray-200 absolute inset-0" />

                                        {/* Half-filled star overlay */}
                                        {fill === 'half' && (
                                            <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                                                <Star className="w-10 h-10 text-yellow-400 fill-yellow-400" />
                                            </div>
                                        )}

                                        {/* Full star overlay */}
                                        {fill === 'full' && (
                                            <Star className="w-10 h-10 text-yellow-400 fill-yellow-400 absolute inset-0" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-3 h-5">
                            {activeRating > 0 && (
                                <span className="text-xs font-bold text-yellow-600 uppercase tracking-widest">
                                    {activeRating} ★ — {getRatingLabel(activeRating)}
                                </span>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 mb-2 uppercase tracking-widest">{t('reviews.feedbackOptional', 'Feedback Comment (Optional)')}</label>
                        <textarea
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:border-brand-500 focus:ring-brand-500 text-sm p-3 min-h-[100px] outline-none transition-all"
                            placeholder={t('reviews.shareExperience', 'Share your experience...')}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                    </div>

                    <Button
                        fullWidth
                        type="submit"
                        disabled={rating === 0 || isSubmitting}
                        className="bg-brand-600 text-white font-bold rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:bg-brand-700 hover:shadow-[0_8px_30px_rgb(0,0,0,0.16)] transition-all hover:-translate-y-0.5 border-transparent h-12"
                    >
                        {isSubmitting ? t('reviews.submitting', 'Submitting Review...') : rating > 0 ? t('reviews.submitStarReview', 'Submit {{rating}} Star Review', { rating }) : t('reviews.selectRatingBtn', 'Select a Rating')}
                    </Button>
                </form>
            </div>
        </div>
    );
};



